// MusterRoll
import { useEffect, useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { MUSTER_ROLL_REPORT } from "../../constants/api";
import { EmployeeDetails } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "lucide-react";

const MusterRoll = () => {

    const [musterRollData, setMusterRollData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [dynamicColumns, setDynamicColumns] = useState([]);

    const [fetchFilter, setFetchFilter] = useState({})

    let navigate = useNavigate()
    const statusConfig = {
        "present": { label: "P", bgColor: "#28A745" },
        "absent": { label: "A", bgColor: "#A72828" },
        // Transferred-day labels (a temporary transfer, worked at another branch).
        // Backend sends these via `display_status`; stored status stays present/absent.
        "TP": { label: "TP", bgColor: "#1E7B34" },
        "TA": { label: "TA", bgColor: "#7B1E1E" },
        "holiday": { label: "HD", bgColor: "#EEBA35" },
        "on-duty": { label: "OD", bgColor: "#3595EE" },
        "week-off": { label: "WO", bgColor: "#F9827A" },
        "compensation": { label: "Comp", bgColor: "#FEB8C3" },
        "WFH": { label: "WFH", bgColor: "#8DD6F5" },
        "leave": { label: "L", bgColor: "#CCA6FC" },
        "N/A": { label: "N/A", bgColor: "#b4b1b1" },
    };

    useEffect(() => {
        if (!fetchFilter?.month || !fetchFilter?.year) return;

        // Combine year + month into a valid date
        const parsedDate = dayjs(`${fetchFilter.year}-${fetchFilter.month}`, "YYYY-M");

        const daysInMonth = parsedDate.daysInMonth();
        const newColumns = [];

        for (let day = 1; day <= daysInMonth; day++) {
            const columnDate = parsedDate.date(day).format("YYYY-MM-DD");

            const columnConfig = {
                accessor: columnDate,
                Header: day.toString(), // just day number
                align: "center",
                Cell: ({ row }) => {
                    const record = row.original ?? row; // adjust for react-table vs antd
                    const attendanceData = record?.attendance_dict?.find(
                        (item) => item.date === columnDate
                    );

                    // Prefer display_status (carries TP/TA for transferred days); fall
                    // back to the raw status for older responses / normal days.
                    const status = attendanceData ? (attendanceData.display_status ?? attendanceData.status) : "N/A";
                    const statusInfo = statusConfig[status] || statusConfig["N/A"];
                    const lateMinute = attendanceData ? attendanceData.LM : "";

                    return (
                        <div>
                            <p className="musterStatusTxt" style={{ color: statusInfo.bgColor }}>
                                {statusInfo?.label}
                            </p>
                            <p className="musterStatusTxt" style={{ color: "#999292" }}>
                                {lateMinute ? `${lateMinute} LM` : ""}
                            </p>
                        </div>
                    );
                },
            };

            newColumns.push(columnConfig);
        }

        setDynamicColumns(newColumns);
    }, [fetchFilter?.month, fetchFilter?.year]);


    // Final Muster Roll Columns
    const MusterRollColumns = [
        {
            accessor: "employee_name",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            maxWidth: "300px",
            Cell: ({ value, row }) => {
                const record = row.original || row;

                let empDetails = {
                    ...record?.employee,
                    designation_id: record?.employee?.designation
                        ? {
                            id: record?.employee.designation.id,
                            designation_name: record?.employee.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.employee?.employee_name || "-",
                    id: record?.employee?.employee_id || "-",
                };

                return <EmployeeDetails details={empDetails} size="xs" />;
            },
        },
        {
            accessor: "present",
            Header: "Present",
            sortable: true,
            align: "center",
            Cell: ({ value }) => (
                <Button
                    variant="outline"
                    className="text-green-600 border-green-200 bg-green-50"
                >
                    {value}
                </Button>
            ),
        },
        {
            accessor: "absent",
            Header: "Absent",
            sortable: true,
            align: "center",
            Cell: ({ value }) => (
                <Button
                    variant="outline"
                    className="text-red-600 border-red-200 bg-red-50"
                >
                    {value}
                </Button>
            ),
        },
        ...dynamicColumns, // append dynamically generated day columns
    ];


    return (
        <div className="p-4 dark:bg-gray-900 rounded-lg dark:shadow-md font-sans">
            <div className="flex justify-between items-center mb-4">
                <span className=" flex gap-2 items-center">
                    <ChevronLeftIcon onClick={() => { navigate('/reports') }} className=" cursor-pointer" />
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                        Muster Roll Report ({totalRecords})
                    </h1>
                </span>
            </div>

            <Reactable
                columns={MusterRollColumns}
                data={musterRollData}
                setData={setMusterRollData}
                setTotalCount={setTotalRecords}
                setFetchFilter={setFetchFilter}
                url={MUSTER_ROLL_REPORT}
                serverSide={true}
                tableHeight="calc(100vh - 310px)"
                rowSize="sm"
                isCSVRequired={true}
                isMonthYearRequired={true}
            />
        </div >
    );
}

export default MusterRoll;